import { Organization } from "../../../../core/models/entities/Organization";

export type OrgSectionId =
  | "details"
  | "shipping"
  | "pricing"
  | "brands"
  | "users";

export interface OrganizationUpdateForm {
  id: number;
  name: string;
  email: string;
  phone: string;
  address: string;
  postCode: string;
  city: string;
  country: string;
  cvr: string;
  eori?: string;
  contactPerson: string;
  contactPhone: string;
  contactEmail: string;
  /** Ids only — the manufacturers master list is loaded lazily. */
  excludedManufacturerIds: number[];
  shippingName?: string;
  shippingAddress?: string;
  shippingPostCode?: string;
  shippingCity?: string;
  shippingCountry?: string;
  shippingContactPerson?: string;
  shippingContactPhone?: string;
  shippingContactEmail?: string;
  vatRate?: number;
  hidePrices?: boolean;
  disabled?: boolean;
  language?: string | null;
}

export function toOrganizationUpdateForm(
  customer: Organization | undefined,
  fallbackVatRate?: number
): OrganizationUpdateForm {
  return {
    id: customer?.id ?? 0,
    postCode: customer?.postCode ?? "",
    address: customer?.address ?? "",
    city: customer?.city ?? "",
    country: customer?.country ?? "",
    cvr: customer?.cvr ?? "",
    email: customer?.email ?? "",
    contactEmail: customer?.contactEmail ?? "",
    phone: customer?.phone ?? "",
    contactPhone: customer?.contactPhone ?? "",
    contactPerson: customer?.contactPerson ?? "",
    name: customer?.name ?? "",
    excludedManufacturerIds: customer?.excludedManufacturers ?? [],
    eori: customer?.eori ?? "",
    shippingName: customer?.shippingName ?? "",
    shippingAddress: customer?.shippingAddress ?? "",
    shippingPostCode: customer?.shippingPostCode ?? "",
    shippingCity: customer?.shippingCity ?? "",
    shippingCountry: customer?.shippingCountry ?? "",
    shippingContactPerson: customer?.shippingContactPerson ?? "",
    shippingContactPhone: customer?.shippingContactPhone ?? "",
    shippingContactEmail: customer?.shippingContactEmail ?? "",
    vatRate: customer?.vatRate ?? fallbackVatRate,
    hidePrices: customer?.hidePrices ?? false,
    // Round-trip fields: the backend overwrites omitted fields with defaults
    //  (false/null), so they must always be sent back.
    disabled: customer?.disabled ?? false,
    language: customer?.language ?? null,
  };
}

/**
 * True when the organization has a shipping address that differs from the
 * company address (drives the "shipping differs" checkbox initial state).
 */
export function hasDifferingShippingAddress(customer?: Organization): boolean {
  if (!customer) return false;
  const pairs: [string | undefined, string | undefined][] = [
    [customer.shippingName, customer.name],
    [customer.shippingAddress, customer.address],
    [customer.shippingPostCode, customer.postCode],
    [customer.shippingCity, customer.city],
    [customer.shippingCountry, customer.country],
    [customer.shippingContactPerson, customer.contactPerson],
    [customer.shippingContactPhone, customer.phone],
    [customer.shippingContactEmail, customer.email],
  ];
  const anyFilled = pairs.some(([shipping]) => !!shipping);
  if (!anyFilled) return false;
  return pairs.some(([shipping, company]) => (shipping ?? "") !== (company ?? ""));
}

/**
 * Resolves the shipping fields for the save body.
 *
 * - Still differing (or untouched): send the form values as-is, so unrelated
 *   saves never alter stored shipping data.
 * - Actively unchecked (was differing, now not): mirror the company address
 *   into the shipping fields — the exact behavior of the old "use same
 *   shipping details as invoice" checkbox.
 */
export function resolveShippingFields(
  form: OrganizationUpdateForm,
  shippingDiffers: boolean,
  wasDiffering: boolean
): OrganizationUpdateForm {
  if (shippingDiffers || !wasDiffering) return form;
  return {
    ...form,
    shippingName: form.name,
    shippingAddress: form.address,
    shippingPostCode: form.postCode,
    shippingCity: form.city,
    shippingCountry: form.country,
    shippingContactPerson: form.contactPerson,
    shippingContactPhone: form.phone,
    shippingContactEmail: form.email,
  };
}

export function isCustomerUpdateFormInvalid(
  form: OrganizationUpdateForm
): boolean {
  return (
    !form.name ||
    !form.cvr ||
    !form.phone ||
    !form.address ||
    !form.email ||
    !form.contactEmail ||
    !form.contactPerson ||
    !form.contactPhone
  );
}
